<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue'
import { cva } from 'class-variance-authority'
import { PhCaretUp, PhCaretDown } from '@phosphor-icons/vue'
import { useMe } from '@/queries/me'
import BIcon from '@/components/BIcon.vue'
import SideNavBadge from '@/components/SideNavBadge.vue'
import type { SideNavItem } from '@/types/sideNav'
import { SpButton } from '@tithely/sproutkit-vue'

interface Props {
    nav: SideNavItem[]
    maxVisibleItems?: number
}

const props = withDefaults(defineProps<Props>(), {
    maxVisibleItems: undefined
})

const { hasTrinity: trinity } = useMe()

const isExpanded = ref(false)

const shouldShowAccordion = computed(() => {
    return props.maxVisibleItems !== undefined && props.nav.length > props.maxVisibleItems
})

const visibleItems = computed(() => {
    if (!shouldShowAccordion.value) {
        return props.nav
    }

    if (isExpanded.value) {
        return props.nav
    }

    return props.nav.slice(0, props.maxVisibleItems!)
})

const toggleAccordion = () => {
    isExpanded.value = !isExpanded.value
}

const current = (itemRoute: string) => itemRoute === window.location.pathname

onMounted(() => {
    if (shouldShowAccordion.value && !isExpanded.value) {
        const currentRouteIndex = props.nav.findIndex(item => current(item.route))
        if (currentRouteIndex >= 0 && currentRouteIndex >= props.maxVisibleItems!) {
            isExpanded.value = true
        }
    }
})

const navClasses = cva(`relative shrink-0 grow-0 max-lg:hidden lg:block lg:print:hidden`, {
    variants: {
        trinity: {
            true: `bg-primary-50 !px-0 !py-4`,
            false: `bg-secondary-50 !pt-[30px]`
        }
    }
})

const listClasses = cva(`relative`, {
    variants: {
        current: {
            true: ``
        },
        trinity: {
            true: ``
        }
    },
    compoundVariants: [
        {
            current: true,
            trinity: false,
            class: `before:absolute before:left-0 before:top-0 before:h-full before:w-1.5 before:rounded-r-[20px] before:bg-primary`
        }
    ]
})

const linkClasses = cva(`group flex items-center transition-opacity`, {
    variants: {
        current: {
            true: `pointer-events-none opacity-100`,
            false: `hover:opacity-100`
        },
        trinity: {
            true: `min-h-9 text-sm font-semibold text-primary-950 hover:bg-primary/10`,
            false: `h-[38px] gap-4 !px-[22px] font-bold text-primary`
        }
    },
    compoundVariants: [
        {
            current: false,
            trinity: false,
            class: `opacity-40 `
        },
        {
            current: false,
            trinity: true,
            class: `opacity-75`
        },
        {
            current: true,
            trinity: true,
            class: `bg-primary/20`
        }
    ]
})

const iconWrapperClasses = cva(`relative flex shrink-0 items-center justify-center`, {
    variants: {
        trinity: {
            true: `w-14 `
        }
    }
})

const iconClasses = cva([], {
    variants: {
        trinity: {
            true: `size-5`,
            false: `size-6`
        }
    }
})
</script>

<template>
    <nav :class="navClasses({ trinity })">
        <ul class="flex flex-col gap-4">
            <TransitionGroup
                enter-active-class="transition-all duration-300"
                leave-active-class="transition-all duration-300"
                enter-from-class="-translate-y-4 opacity-0"
                leave-to-class="-translate-y-4 opacity-0"
                move-class="transition-transform duration-300">
                <li
                    v-for="item in visibleItems"
                    :key="item.route"
                    :class="listClasses({ current: current(item.route), trinity })">
                    <a
                        :href="item.route"
                        :class="linkClasses({ current: current(item.route), trinity })">
                        <span :class="iconWrapperClasses({ trinity })">
                            <BIcon
                                :icon="item.icon"
                                :class="iconClasses({ trinity })" />
                        </span>

                        <span class="flex-1">{{ item.label }}</span>

                        <SideNavBadge
                            v-if="item.count !== undefined"
                            :count="item.count"
                            :trinity="trinity" />
                    </a>
                </li>
            </TransitionGroup>

            <!-- Accordion Toggle Button -->
            <li
                v-if="shouldShowAccordion"
                class="flex w-full justify-start border-t pt-7">
                <SpButton
                    aria-label="Toggle Navigation"
                    @click="toggleAccordion"
                    variant="text"
                    :class="linkClasses({ current: false, trinity })">
                    <PhCaretUp
                        v-if="isExpanded"
                        class="size-5 transition-transform duration-200" />
                    <PhCaretDown
                        v-else
                        class="size-5 transition-transform duration-200" />
                    <span class="pl-1 font-bold">
                        {{ isExpanded ? 'View Less' : `View More` }}
                    </span>
                </SpButton>
            </li>
        </ul>
    </nav>
</template>
